/**
 * The aircraft card: click a blip or strip and a flight-strip card opens beside it with what the
 * pilot wants, how long they have waited, what to say, and keys that transmit at once (the
 * pilot's readback is the confirmation). It follows the blip and re-reads the sim while open, so
 * it shows the aircraft as it is now. Wiring and markup only; the keys come from card-actions.ts.
 */
import { toMagneticDeg, type AircraftState } from '@atc/core';
import { NATURE_TEXT, NEEDS, type Sim } from '@atc/sim';
import { phraseFor } from './assist';
import { cardActions } from './card-actions';
import { hdg3 } from './hints';

export interface AircraftCard {
  select(callsign: string | null): void;
  /** Re-read the selected aircraft and follow its blip; call on every sim tick. */
  update(states: readonly AircraftState[]): void;
  setAssists(on: boolean): void;
  setEnabled(on: boolean): void;
  /** Whether push to talk is off, ready, or keyed right now; the card says who the radio is pointed at. */
  setSpeech(state: SpeechState): void;
  isOpen(): boolean;
  destroy(): void;
}

export type SpeechState = 'off' | 'ready' | 'live';

/** Keys and text are rebuilt every this many ticks (and at once after a transmission); position and clock every tick. */
const RENDER_EVERY = 4;
/** Pixels between the blip and the card, clear of the data block drawn up and to the right. */
const GAP = 22;

const PHASE: Record<string, string> = {
  holdShort: 'holding short', lineUp: 'lining up', linedUp: 'lined up', takeoffRoll: 'rolling', abortRoll: 'stopping',
  departure: 'climbing out', enroute: 'en route', approach: 'approach', final: 'final', goAround: 'going around',
  rollout: 'rollout', vacating: 'vacating', landed: 'landed', handedOff: 'handed off', exited: 'left airspace', cancelled: 'to stand', forcedLanding: 'out of fuel',
};
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const clock = (sec: number) => { const v = Math.max(0, Math.floor(sec)); return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`; };
const feet = (ft: number) => `${(Math.round(ft / 100) * 100).toLocaleString('en-US')} ft`;

export function createAircraftCard(host: HTMLElement, scopeEl: HTMLElement, sim: Sim, scope: { screenPosition(callsign: string): { x: number; y: number } | null }, h: { onTransmit(command: string): void; onClose(): void }): AircraftCard {
  const el = document.createElement('section');
  el.className = 'acard';
  el.hidden = true;
  el.setAttribute('aria-label', 'Selected aircraft');
  host.appendChild(el);
  let callsign: string | null = null, assists = true, enabled = true, speech: SpeechState = 'off';
  let tick = 0, dirty = true, shown = '', hovered = false;

  // Hold still under the pointer so a moving blip never drags a key away from a click.
  el.addEventListener('pointerenter', () => { hovered = true; });
  el.addEventListener('pointerleave', () => { hovered = false; });
  el.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button');
    if (!b) return;
    if (b.dataset.act === 'close') { h.onClose(); return; }
    if (!b.dataset.cmd || b.getAttribute('aria-disabled') === 'true' || !enabled) return;
    h.onTransmit(b.dataset.cmd);
    dirty = true;
  });

  function render(s: AircraftState): void {
    const ac = sim.find(s.callsign);
    if (!ac) return;
    const req = ac.pilot.request && !ac.pilot.request.unvoiced ? ac.pilot.request : undefined;
    // sim.internal() is the live traffic; cardActions only reads it, to see whether the runway is free.
    const actions = cardActions(ac, s, sim.airport, sim.internal(), { approachRole: sim.approachRole });
    // Assist decides whether the suggested key is *lit*; the card always says what the key would
    // sound like on the radio, because that is how you learn the phraseology.
    const primary = assists ? actions.find((a) => a.primary) : undefined;
    const spoken = actions.find((a) => a.primary) ?? actions[0];
    const say = spoken ? phraseFor(sim, spoken.command) : null;
    const a = s.assigned ?? {};
    const cleared = [a.hold ? `hold ${a.hold}` : a.fix ? `direct ${a.fix}` : a.headingDeg !== undefined ? `heading ${hdg3(a.headingDeg)}` : '', a.altitudeFt !== undefined ? feet(a.altitudeFt) : '', a.runway ? `runway ${a.runway}` : ''].filter(Boolean).join(', ');
    const route = [s.procedure ? `${s.kind === 'departure' ? 'SID' : 'STAR'} ${s.procedure}` : '', s.approach ?? ''].filter(Boolean).join(' · ');
    // In trouble: what it is, how many people are on board and how long the fuel lasts.
    const trouble = [
      ac.emergency ? `${ac.emergency.level === 'mayday' ? 'MAYDAY' : 'PAN PAN'} · ${NATURE_TEXT[ac.emergency.nature]} · ${ac.emergency.persons} on board` : '',
      ac.fuel && ac.fuel.state !== 'normal' ? `${Math.round(ac.fuel.enduranceMin)} min of fuel` : '',
    ].filter(Boolean).join(' · ');
    const keys = actions.map((k) => {
      // The tooltip is what you would say for that key, or why the pilot would refuse it.
      const phrase = phraseFor(sim, k.command);
      const title = k.disabledReason ? `Pilot would say: unable, ${k.disabledReason}` : phrase ? `Say: "${phrase}"` : k.command;
      return `<button type="button" class="acard-key${k === primary ? ' primary' : ''}" data-cmd="${esc(k.command)}" title="${esc(title)}"${k.disabledReason || !enabled ? ' aria-disabled="true"' : ''}><span>${esc(k.label)}</span><code>${esc(k.command.slice(s.callsign.length + 1))}</code></button>`;
    }).join('');
    // The radio banner: with an aircraft picked, talking reaches it even if the transcript loses
    // the callsign (voice-resolve.ts), so the card says so where the controller is already looking.
    const mic = speech === 'off' || !enabled ? ''
      : `<p class="acard-mic${speech === 'live' ? ' live' : ''}"><small>Radio</small>${speech === 'live' ? `Transmitting to ${esc(s.callsign)}` : `Hold Space to speak to ${esc(s.callsign)}`}</p>`;
    const html = `<header class="acard-head ${s.kind ?? 'arrival'}"><b>${esc(s.callsign)}</b><span>${esc(s.type)}</span><span class="acard-phase">${esc(PHASE[s.phase ?? ''] ?? s.phase ?? '')}</span><button type="button" class="acard-close" data-act="close" aria-label="Close aircraft card" title="Close (Esc)">×</button></header>
      ${mic}
      <p class="acard-now"><span data-live="now"></span>${cleared ? `<span class="acard-cleared">Cleared ${esc(cleared)}</span>` : ''}</p>
      ${route ? `<p class="acard-route">${esc(route)}</p>` : ''}
      ${trouble ? `<p class="acard-emerg">${esc(trouble)}</p>` : ''}
      ${req ? `<p class="acard-wants stage-${Math.min(2, req.stage)}"><span>Wants <b>${esc(NEEDS[req.kind].text)}</b></span><time data-live="waited"></time></p>` : '<p class="acard-wants none">No request waiting</p>'}
      ${say ? `<p class="acard-say"><small>Say</small><q>${esc(say)}</q></p>` : ''}
      ${keys ? `<div class="acard-keys">${keys}</div>` : ''}
      <p class="acard-foot">${!enabled ? 'Resume to transmit.' : keys ? 'Click to transmit, or type or say anything else.' : 'No longer on your frequency.'}</p>`;
    // Replace the markup only when it changed, so a key under the pointer stays the same element.
    if (html !== shown) { shown = html; el.innerHTML = html; }
  }

  const setText = (sel: string, text: string) => { const n = el.querySelector(sel); if (n && n.textContent !== text) n.textContent = text; };
  function live(s: AircraftState): void {
    setText('[data-live="now"]', s.onGround ? `${Math.round(s.groundspeedKt)} kt` : `${feet(s.altitudeFt)} · ${Math.round(s.groundspeedKt)} kt · ${hdg3(toMagneticDeg(sim.airport, s.headingDeg))}`);
    const req = sim.find(s.callsign)?.pilot.request;
    if (req) setText('[data-live="waited"]', clock(sim.simTimeS - req.askedAtS));
  }

  function place(): void {
    if (!callsign || hovered) return;
    const pos = scope.screenPosition(callsign);
    const sr = scopeEl.getBoundingClientRect(), hr = host.getBoundingClientRect();
    const docked = !pos || sr.width < 120 || sr.height < 120;
    el.classList.toggle('docked', docked);
    if (docked) { el.style.left = el.style.top = ''; return; }
    const ox = sr.left - hr.left, oy = sr.top - hr.top, w = el.offsetWidth, ht = el.offsetHeight;
    const bx = ox + pos.x, by = oy + pos.y;
    let left = bx + GAP, top = by + GAP;
    if (left + w > ox + sr.width - 8) left = bx - GAP - w;
    if (top + ht > oy + sr.height - 8) top = by - GAP - ht;
    left = Math.max(ox + 8, Math.min(left, ox + sr.width - w - 8));
    top = Math.max(oy + 8, Math.min(top, oy + sr.height - ht - 8));
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
  }

  const api: AircraftCard = {
    select(cs) {
      if (cs === callsign) return;
      callsign = cs; hovered = false; shown = ''; dirty = true;
      el.hidden = !cs;
      if (cs) api.update(sim.aircraft());
    },
    update(states) {
      if (!callsign) return;
      const s = states.find((x) => x.callsign === callsign);
      if (!s) return;
      if (dirty || ++tick % RENDER_EVERY === 0) { dirty = false; render(s); }
      live(s);
      place();
    },
    setAssists(on) { assists = on; dirty = true; },
    setSpeech(state) { if (state !== speech) { speech = state; dirty = true; } },
    setEnabled(on) { if (on !== enabled) { enabled = on; dirty = true; } },
    isOpen: () => callsign !== null,
    destroy: () => el.remove(),
  };
  return api;
}
